import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { getGclid, getSource } from "@/utils/gclid";
import {
  Phone, WhatsappLogo, CheckCircle, Clock, ShieldCheck, MapPin, Star,
  ArrowRight, Wrench, Drop, Lightning, Gear, Warning, SealCheck,
  Timer, CurrencyInr, Medal, CaretDown, Headset, Package,
  Checks, Lock, Plugs, Thermometer, Snowflake, Fan, Monitor
} from "@phosphor-icons/react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PHONE = "9742119056";
const WA = `https://wa.me/91${PHONE}?text=Hi%2C%20I%20need%20side-by-side%20refrigerator%20repair%20in%20Bangalore`;

const PROBLEMS = [
  { icon: Thermometer, title: "Refrigerator Not Cooling", desc: "Food spoiling? Warm fridge compartment? Our technicians diagnose and repair cooling issues quickly." },
  { icon: Snowflake, title: "Freezer Not Freezing", desc: "Ice buildup, weak cooling, or freezer temperature problems fixed on-site." },
  { icon: Drop, title: "Water Leakage", desc: "Water leaking from inside or underneath the refrigerator." },
  { icon: Gear, title: "Ice Maker Problems", desc: "Ice maker not working, slow ice production, or blocked water lines." },
  { icon: Monitor, title: "Display Panel Issues", desc: "Touch panel, temperature display, and control board repairs." },
  { icon: Lightning, title: "Compressor Problems", desc: "Noisy compressor, overheating, or complete cooling failure." },
];

const BRAND_FEATURES = [
  { brand: "LG", desc: "Expert repair for inverter compressor, cooling, and display issues.", color: "from-red-500 to-red-600" },
  { brand: "Samsung", desc: "Repairs for Twin Cooling, digital inverter, and ice maker systems.", color: "from-blue-500 to-blue-600" },
  { brand: "Whirlpool", desc: "Cooling issues, water leakage, thermostat, and compressor repairs.", color: "from-green-500 to-green-600" },
];

const OTHER_BRANDS = ["Bosch", "Haier", "Panasonic", "Hitachi", "Godrej", "Siemens"];

const WHY_US = [
  { icon: Clock, title: "Same-Day Visit Available" },
  { icon: SealCheck, title: "Experienced Refrigerator Technicians" },
  { icon: Package, title: "Genuine Replacement Parts" },
  { icon: CurrencyInr, title: "Transparent Pricing" },
  { icon: Headset, title: "Doorstep Service" },
  { icon: ShieldCheck, title: "Warranty on Service" },
];

const AREAS = [
  "Whitefield", "Electronic City", "HSR Layout", "BTM Layout", "Koramangala",
  "JP Nagar", "Jayanagar", "Marathahalli", "Indiranagar", "Hebbal",
  "Yelahanka", "Rajajinagar"
];

const REVIEWS = [
  { name: "Arun K.", area: "Whitefield", text: "LG side-by-side fridge stopped cooling. Technician fixed it within one visit. Very professional service.", rating: 5 },
  { name: "Meena R.", area: "Koramangala", text: "Samsung refrigerator ice maker issue solved professionally. They had the right parts and finished in 2 hours.", rating: 5 },
  { name: "Vijay P.", area: "HSR Layout", text: "Quick response and reasonable pricing. My Whirlpool side-by-side is working perfectly now. Highly recommend!", rating: 5 },
];

const FAQS = [
  { q: "How quickly can a technician arrive for side-by-side fridge repair?", a: "We offer same-day service across Bangalore. Most repairs are completed within 30-60 minutes of the technician's arrival." },
  { q: "Do you carry spare parts for side-by-side refrigerators?", a: "Yes, our technicians carry common spare parts for LG, Samsung, Whirlpool and other major brands. For specialized parts, we arrange delivery within 24-48 hours." },
  { q: "Is the inspection charge adjustable?", a: "Yes! The ₹350 inspection fee is adjusted against the final repair bill if you proceed with the repair." },
  { q: "Which side-by-side refrigerator brands do you service?", a: "We repair all major brands — LG, Samsung, Whirlpool, Bosch, Haier, Panasonic, Hitachi, Godrej, Siemens and more." },
  { q: "Do you provide warranty on side-by-side refrigerator repairs?", a: "Absolutely. All our repairs come with a warranty covering both parts and labour." },
];

function Fade({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 24 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay }} className={className}>
      {children}
    </motion.div>
  );
}

// ─── HEADER ───
function SBSHeader() {
  return (
    <header data-testid="sbs-header" className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-100 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-16">
        <span className="font-['Poppins'] font-bold text-[#0F172A] text-base uppercase tracking-tight">
          Home Tech Repair
        </span>
        <a href={`tel:${PHONE}`} data-testid="sbs-header-call"
          className="bg-[#FF6B00] text-white hover:bg-[#E55F00] font-bold text-sm px-5 py-2.5 rounded-full transition-all shadow-lg shadow-orange-200 flex items-center gap-1.5 font-['Poppins']">
          <Phone size={16} weight="fill" /> Call Now
        </a>
      </div>
    </header>
  );
}

// ─── HERO ───
function SBSHero() {
  return (
    <section data-testid="sbs-hero" className="pt-16 bg-[#F8FAFC] overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 bg-[#FF6B00]/10 text-[#FF6B00] text-xs font-bold px-4 py-2 rounded-full mb-5 font-['Poppins']">
              <Checks size={16} weight="fill" /> Same-Day Service Available
            </motion.div>
            <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
              className="font-['Poppins'] text-4xl sm:text-5xl lg:text-[3.2rem] font-extrabold text-[#0F172A] leading-[1.1] tracking-tight mb-5">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF6B00] to-[#FF8C00]">Side-by-Side Refrigerator</span>{" "}
              Repair Service in Bangalore
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}
              className="text-base sm:text-lg text-slate-500 leading-relaxed mb-6 max-w-lg font-['Poppins']">
              <strong className="text-[#0F172A]">LG, Samsung, Whirlpool</strong> & all major brands repaired at your doorstep. Same-day service available.
            </motion.p>

            {/* Trust badges */}
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }}
              className="flex flex-wrap gap-x-5 gap-y-2 mb-8 text-sm text-slate-600 font-['Poppins']">
              {["Same Day Service", "Expert Refrigerator Technicians", "Genuine Spare Parts", "Service Warranty", "Transparent Pricing"].map((b, i) => (
                <span key={i} className="flex items-center gap-1.5"><CheckCircle size={16} weight="fill" className="text-[#22C55E]" /> {b}</span>
              ))}
            </motion.div>

            {/* CTAs */}
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-3">
              <a href={`tel:${PHONE}`} data-testid="sbs-hero-call"
                className="bg-[#FF6B00] text-white hover:bg-[#E55F00] font-bold text-base px-8 py-4 rounded-2xl shadow-xl shadow-orange-200 transition-all flex items-center justify-center gap-2 font-['Poppins']">
                <Phone size={20} weight="fill" /> Call Now: {PHONE}
              </a>
              <a href={WA} target="_blank" rel="noopener noreferrer" data-testid="sbs-hero-wa"
                className="bg-[#22C55E] text-white hover:bg-[#16A34A] font-bold text-base px-8 py-4 rounded-2xl shadow-xl shadow-green-200 transition-all flex items-center justify-center gap-2 font-['Poppins']">
                <WhatsappLogo size={20} weight="fill" /> Book a Technician Today
              </a>
            </motion.div>
          </div>

          {/* Right: Form */}
          <motion.div initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.3 }}>
            <SBSForm />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ─── FORM ───
function SBSForm() {
  const [form, setForm] = useState({ name: "", phone: "", area: "", service: "" });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const problems = ["Not Cooling", "Freezer Not Freezing", "Water Leakage", "Ice Maker Problem", "Display Panel Issue", "Compressor Problem", "Other"];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.area || !form.service) { toast.error("Please fill all fields"); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/leads`, { ...form, gclid: getGclid(), source: getSource() });
      if (res.data.success) { navigate(`/thank-you?name=${encodeURIComponent(form.name)}&service=side-by-side-fridge`); }
    } catch { toast.error("Something went wrong. Please call us."); }
    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} data-testid="sbs-booking-form" className="bg-white p-6 sm:p-8 rounded-3xl shadow-2xl border border-slate-100">
      <h3 className="font-['Poppins'] text-xl font-bold text-[#0F172A] mb-1">Book a Technician</h3>
      <p className="text-xs text-slate-400 font-['Poppins'] mb-5">We'll call you back in 30 minutes</p>
      <div className="space-y-3">
        <input data-testid="sbs-form-name" type="text" placeholder="Your Name" value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3.5 text-sm font-['Poppins'] focus:border-[#FF6B00] focus:ring-2 focus:ring-[#FF6B00]/10 outline-none transition-all" />
        <input data-testid="sbs-form-phone" type="tel" placeholder="Mobile Number" value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
          className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3.5 text-sm font-['Poppins'] focus:border-[#FF6B00] focus:ring-2 focus:ring-[#FF6B00]/10 outline-none transition-all" />
        <input data-testid="sbs-form-area" type="text" placeholder="Your Area in Bangalore" value={form.area}
          onChange={(e) => setForm({ ...form, area: e.target.value })}
          className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3.5 text-sm font-['Poppins'] focus:border-[#FF6B00] focus:ring-2 focus:ring-[#FF6B00]/10 outline-none transition-all" />
        <Select value={form.service} onValueChange={(val) => setForm({ ...form, service: val })}>
          <SelectTrigger data-testid="sbs-form-service" className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3.5 h-auto text-sm font-['Poppins'] focus:ring-2 focus:ring-[#FF6B00]/10">
            <SelectValue placeholder="Select Problem" />
          </SelectTrigger>
          <SelectContent>
            {problems.map(p => <SelectItem key={p} value={`sbs-${p.toLowerCase().replace(/[\s\/]/g, "-")}`}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <button type="submit" disabled={loading} data-testid="sbs-booking-submit"
        className="w-full mt-5 bg-[#FF6B00] text-white hover:bg-[#E55F00] font-bold text-base px-8 py-4 rounded-2xl shadow-xl shadow-orange-200 transition-all flex items-center justify-center gap-2 font-['Poppins'] disabled:opacity-50">
        {loading ? "Booking..." : "Book a Technician Today"} {!loading && <ArrowRight size={18} weight="bold" />}
      </button>
    </form>
  );
}

// ─── PROBLEMS WE FIX ───
function ProblemsSection() {
  return (
    <section data-testid="sbs-problems" className="bg-white py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <Fade>
          <div className="text-center mb-12">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#FF6B00] mb-3 block font-['Poppins']">Expert Diagnosis & Repair</span>
            <h2 className="font-['Poppins'] text-3xl sm:text-4xl font-extrabold text-[#0F172A] tracking-tight">Common Side-by-Side Refrigerator Problems We Fix</h2>
          </div>
        </Fade>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {PROBLEMS.map((p, i) => (
            <Fade key={i} delay={i * 0.06}>
              <div data-testid={`sbs-problem-${i}`}
                className="bg-[#F8FAFC] rounded-2xl p-6 border border-slate-100 hover:shadow-lg hover:-translate-y-1 transition-all group">
                <div className="w-12 h-12 bg-[#FF6B00]/10 rounded-2xl flex items-center justify-center mb-4 group-hover:bg-[#FF6B00]/20 transition-colors">
                  <p.icon size={24} weight="duotone" className="text-[#FF6B00]" />
                </div>
                <h4 className="font-['Poppins'] text-base font-bold text-[#0F172A] mb-2">{p.title}</h4>
                <p className="text-sm text-slate-500 font-['Poppins'] leading-relaxed">{p.desc}</p>
              </div>
            </Fade>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── BRANDS WE REPAIR ───
function BrandsSection() {
  return (
    <section data-testid="sbs-brands" className="bg-[#F8FAFC] py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <Fade>
          <div className="text-center mb-12">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#FF6B00] mb-3 block font-['Poppins']">Brand Specialists</span>
            <h2 className="font-['Poppins'] text-3xl sm:text-4xl font-extrabold text-[#0F172A] tracking-tight">Brands We Repair</h2>
          </div>
        </Fade>

        {/* Featured brands */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
          {BRAND_FEATURES.map((b, i) => (
            <Fade key={i} delay={i * 0.08}>
              <div data-testid={`sbs-brand-${i}`}
                className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all relative overflow-hidden">
                <div className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-br ${b.color} opacity-10 rounded-bl-[50px]`} />
                <div className={`w-11 h-11 bg-gradient-to-br ${b.color} rounded-2xl flex items-center justify-center mb-4 shadow-lg`}>
                  <Wrench size={20} weight="fill" className="text-white" />
                </div>
                <h3 className="font-['Poppins'] text-lg font-bold text-[#0F172A] mb-2">{b.brand} Side-by-Side Refrigerator Repair</h3>
                <p className="text-sm text-slate-500 font-['Poppins'] leading-relaxed">{b.desc}</p>
              </div>
            </Fade>
          ))}
        </div>

        {/* Other brands */}
        <Fade delay={0.3}>
          <div className="text-center">
            <p className="text-xs font-bold tracking-[0.15em] uppercase text-slate-400 mb-4 font-['Poppins']">Other Brands We Service</p>
            <div className="flex flex-wrap justify-center gap-3">
              {OTHER_BRANDS.map((b, i) => (
                <span key={i} className="bg-white border border-slate-200 rounded-2xl px-5 py-2.5 text-sm font-bold text-[#0F172A] font-['Poppins'] hover:border-[#FF6B00]/30 hover:shadow-md transition-all">
                  {b}
                </span>
              ))}
            </div>
          </div>
        </Fade>
      </div>
    </section>
  );
}

// ─── WHY CHOOSE US ───
function WhyUsSection() {
  return (
    <section data-testid="sbs-whyus" className="bg-[#0F172A] py-16 md:py-24 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-[#FF6B00]/10 via-transparent to-transparent" />
      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8">
        <Fade>
          <div className="text-center mb-12">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#FF6B00] mb-3 block font-['Poppins']">Why Choose Us</span>
            <h2 className="font-['Poppins'] text-3xl sm:text-4xl font-extrabold text-white tracking-tight">Why Bangalore Homeowners Choose Us</h2>
          </div>
        </Fade>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 lg:gap-6">
          {WHY_US.map((w, i) => (
            <Fade key={i} delay={i * 0.06}>
              <div data-testid={`sbs-why-${i}`}
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5 hover:bg-white/10 hover:border-[#FF6B00]/30 transition-all flex items-center gap-3">
                <w.icon size={24} weight="duotone" className="text-[#FF6B00] shrink-0" />
                <h4 className="font-['Poppins'] text-sm font-bold text-white">{w.title}</h4>
              </div>
            </Fade>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── REVIEWS ───
function ReviewsSection() {
  return (
    <section data-testid="sbs-reviews" className="bg-[#F8FAFC] py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <Fade>
          <div className="text-center mb-12">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#FF6B00] mb-3 block font-['Poppins']">Customer Reviews</span>
            <h2 className="font-['Poppins'] text-3xl sm:text-4xl font-extrabold text-[#0F172A] tracking-tight">What Customers Say</h2>
          </div>
        </Fade>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {REVIEWS.map((r, i) => (
            <Fade key={i} delay={i * 0.1}>
              <div data-testid={`sbs-review-${i}`}
                className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm hover:shadow-lg transition-all">
                <div className="flex gap-0.5 mb-3">
                  {[...Array(r.rating)].map((_, j) => <Star key={j} size={16} weight="fill" className="text-amber-400" />)}
                </div>
                <p className="text-sm text-slate-600 font-['Poppins'] leading-relaxed mb-5">"{r.text}"</p>
                <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
                  <div className="w-10 h-10 bg-gradient-to-br from-[#FF6B00] to-[#FF8C00] rounded-full flex items-center justify-center text-white text-xs font-bold">
                    {r.name.split(" ").map(n => n[0]).join("")}
                  </div>
                  <div>
                    <p className="text-sm font-bold text-[#0F172A] font-['Poppins']">{r.name}</p>
                    <p className="text-xs text-slate-400 flex items-center gap-1"><MapPin size={10} weight="fill" /> {r.area}</p>
                  </div>
                  <SealCheck size={20} weight="fill" className="text-[#22C55E] ml-auto" />
                </div>
              </div>
            </Fade>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── SERVICE AREAS ───
function AreasSection() {
  return (
    <section data-testid="sbs-areas" className="bg-white py-16 md:py-20">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <Fade>
          <div className="text-center mb-10">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#FF6B00] mb-3 block font-['Poppins']">Service Areas</span>
            <h2 className="font-['Poppins'] text-3xl sm:text-4xl font-extrabold text-[#0F172A] tracking-tight mb-2">Side-by-Side Refrigerator Repair Across Bangalore</h2>
          </div>
        </Fade>
        <Fade delay={0.1}>
          <div className="flex flex-wrap justify-center gap-2.5 max-w-3xl mx-auto">
            {AREAS.map((a, i) => (
              <span key={i} className="bg-[#F8FAFC] text-slate-600 text-sm font-medium font-['Poppins'] px-5 py-2.5 rounded-full border border-slate-100 shadow-sm hover:border-[#FF6B00]/30 hover:text-[#FF6B00] hover:shadow-md transition-all cursor-default">
                {a}
              </span>
            ))}
          </div>
        </Fade>
      </div>
    </section>
  );
}

// ─── FAQ ───
function FAQSection() {
  const [open, setOpen] = useState(null);
  return (
    <section data-testid="sbs-faq" className="bg-[#F8FAFC] py-16 md:py-24">
      <div className="max-w-3xl mx-auto px-4 md:px-8">
        <Fade>
          <div className="text-center mb-12">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#FF6B00] mb-3 block font-['Poppins']">FAQ</span>
            <h2 className="font-['Poppins'] text-3xl sm:text-4xl font-extrabold text-[#0F172A] tracking-tight">Got Questions?</h2>
          </div>
        </Fade>
        <div className="space-y-3">
          {FAQS.map((faq, i) => (
            <Fade key={i} delay={i * 0.05}>
              <div data-testid={`sbs-faq-${i}`} className="bg-white border border-slate-100 rounded-2xl overflow-hidden transition-all">
                <button onClick={() => setOpen(open === i ? null : i)} className="w-full flex items-center justify-between p-5 text-left">
                  <span className="font-['Poppins'] text-sm font-semibold text-[#0F172A] pr-4">{faq.q}</span>
                  <CaretDown size={18} weight="bold" className={`text-[#FF6B00] shrink-0 transition-transform ${open === i ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence>
                  {open === i && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }}>
                      <p className="px-5 pb-5 text-sm text-slate-500 font-['Poppins'] leading-relaxed">{faq.a}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </Fade>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── EMERGENCY CTA ───
function EmergencyCTA() {
  return (
    <section data-testid="sbs-emergency-cta" className="bg-[#0F172A] py-16 md:py-24 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-[#FF6B00]/15 via-transparent to-transparent" />
      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 text-center">
        <Fade>
          <Warning size={44} weight="duotone" className="text-[#FF6B00] mx-auto mb-4" />
          <h2 className="font-['Poppins'] text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight mb-3 leading-tight">
            Is Your Side-by-Side Refrigerator{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF6B00] to-[#FF8C00]">Not Cooling?</span>
          </h2>
          <p className="text-white/50 font-['Poppins'] text-base sm:text-lg mb-2">Don't risk losing expensive food and groceries.</p>
          <p className="text-white/70 font-['Poppins'] text-sm mb-8">Same-Day Refrigerator Repair in Bangalore</p>
          <a href={`tel:${PHONE}`} data-testid="sbs-emergency-phone"
            className="font-['Poppins'] text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tighter inline-block mb-8 hover:text-[#FF6B00] transition-colors cursor-pointer">
            {PHONE}
          </a>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${PHONE}`} data-testid="sbs-emergency-call"
              className="bg-[#FF6B00] text-white hover:bg-[#E55F00] font-bold text-lg px-10 py-5 rounded-2xl shadow-xl shadow-orange-900/20 transition-all inline-flex items-center justify-center gap-2 font-['Poppins']">
              <Phone size={22} weight="fill" /> Call {PHONE} Now
            </a>
            <a href={WA} target="_blank" rel="noopener noreferrer" data-testid="sbs-emergency-wa"
              className="bg-[#22C55E] text-white hover:bg-[#16A34A] font-bold text-lg px-10 py-5 rounded-2xl shadow-xl transition-all inline-flex items-center justify-center gap-2 font-['Poppins']">
              <WhatsappLogo size={22} weight="fill" /> WhatsApp Us
            </a>
          </div>
        </Fade>
      </div>
    </section>
  );
}

// ─── FOOTER ───
function SBSFooter() {
  return (
    <footer data-testid="sbs-footer" className="bg-[#0F172A] border-t border-white/5 py-8">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div>
            <span className="font-['Poppins'] font-bold text-white text-sm block uppercase tracking-tight">Home Tech Repair</span>
            <span className="text-white/30 text-xs font-['Poppins']">Side-by-Side Refrigerator Repair — Bangalore</span>
          </div>
          <div className="flex items-center gap-5 text-sm text-white/40 font-['Poppins']">
            <a href={`tel:${PHONE}`} className="hover:text-white transition-colors flex items-center gap-1.5"><Phone size={14} weight="fill" /> {PHONE}</a>
            <a href={WA} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors flex items-center gap-1.5"><WhatsappLogo size={14} weight="fill" /> WhatsApp</a>
          </div>
        </div>
        <div className="mt-6 pt-6 border-t border-white/5 text-center text-xs text-white/20 font-['Poppins']">
          Home Tech Repair Bangalore. Side-by-side refrigerator repair for LG, Samsung, Whirlpool, Bosch & more.
        </div>
      </div>
    </footer>
  );
}

// ─── STICKY MOBILE CTA ───
function StickyMobile() {
  return (
    <div data-testid="sbs-sticky-cta" className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-white/90 backdrop-blur-xl border-t border-slate-100 p-2 flex gap-2">
      <a href={`tel:${PHONE}`} className="flex-1 bg-[#FF6B00] text-white font-bold text-sm py-3.5 rounded-xl flex items-center justify-center gap-1.5 shadow-lg shadow-orange-200 font-['Poppins']">
        <Phone size={16} weight="fill" /> Call Now
      </a>
      <a href={WA} target="_blank" rel="noopener noreferrer" className="flex-1 bg-[#22C55E] text-white font-bold text-sm py-3.5 rounded-xl flex items-center justify-center gap-1.5 shadow-lg shadow-green-200 font-['Poppins']">
        <WhatsappLogo size={16} weight="fill" /> WhatsApp
      </a>
    </div>
  );
}

// ─── MAIN ───
export default function SideBySidePage() {
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <SBSHeader />
      <SBSHero />
      <ProblemsSection />
      <BrandsSection />
      <WhyUsSection />
      <ReviewsSection />
      <AreasSection />
      <FAQSection />
      <EmergencyCTA />
      <SBSFooter />
      <StickyMobile />
    </div>
  );
}
